import { useCallback, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Star, GripVertical } from "lucide-react";

/**
 * ImageUploader — drag&drop + preview + reorder + main photo selection.
 * Props:
 *  - value: array of {id, url, content_type} (controlled)
 *  - onChange(next): called with the new array
 *  - max: maximum number of images (default 10)
 */
export default function ImageUploader({ value = [], onChange, max = 10 }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);

  const uploadFiles = useCallback(async (files) => {
    const slots = max - value.length;
    if (slots <= 0) { toast.error(`Maximum ${max} photos`); return; }
    const toUpload = Array.from(files).slice(0, slots);
    setBusy(true);
    try {
      const results = [];
      for (const file of toUpload) {
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
          toast.error(`Format non supporté : ${file.name}`); continue;
        }
        const form = new FormData();
        form.append("file", file);
        try {
          const { data } = await api.post("/uploads/image", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          results.push(data);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Échec upload : ${file.name}`);
        }
      }
      if (results.length) {
        onChange([...value, ...results]);
        toast.success(`${results.length} photo(s) ajoutée(s)`);
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [max, value, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const remove = (i) => onChange(value.filter((_, j) => j !== i));

  const makeMain = (i) => {
    if (i === 0) return;
    const next = [...value];
    const [item] = next.splice(i, 1);
    next.unshift(item);
    onChange(next);
  };

  const onDragStart = (i) => (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; };
  const onDragOverItem = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDropItem = (i) => (e) => {
    e.preventDefault(); e.stopPropagation();
    if (dragIdx === null || dragIdx === i) return;
    const next = [...value];
    const [item] = next.splice(dragIdx, 1);
    next.splice(i, 0, item);
    onChange(next);
    setDragIdx(null);
  };

  return (
    <div data-testid="image-uploader" className="space-y-4">
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileRef.current?.click()}
        data-testid="image-drop-zone"
        className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-accent-brand bg-accent-brand/5" : "border-navy/25 hover:border-accent-brand hover:bg-navy/5"}`}
      >
        <Upload className="mx-auto text-accent-brand mb-3" size={32} strokeWidth={1.4} />
        <p className="text-sm text-navy/70">
          <strong className="text-navy">Cliquez ou déposez</strong> vos photos ici — JPG, PNG, WebP (max 8 Mo/photo, {max} photos max)
        </p>
        {busy && <p className="text-xs text-accent-brand font-bold uppercase tracking-widest mt-2">Upload en cours…</p>}
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => e.target.files && uploadFiles(e.target.files)}
          className="hidden"
          data-testid="image-file-input"
        />
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {value.map((img, i) => (
            <div
              key={img.id}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOverItem}
              onDrop={onDropItem(i)}
              data-testid={`image-thumb-${i}`}
              className="relative group aspect-[4/3] bg-navy/5 border border-navy/10 overflow-hidden"
            >
              <img src={api.defaults.baseURL + `/uploads/${img.id}`} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-colors flex flex-col justify-between p-1.5">
                <div className="flex items-start justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white/95 text-navy text-[10px] font-bold px-1.5 py-0.5 flex items-center gap-1"><GripVertical size={12} /> {i + 1}</div>
                  <button
                    type="button" onClick={() => remove(i)} data-testid={`image-remove-${i}`}
                    className="bg-white/95 text-red-600 p-1 hover:bg-red-600 hover:text-white transition-colors"
                    aria-label="Supprimer la photo"
                  ><X size={14} /></button>
                </div>
                {i !== 0 && (
                  <button
                    type="button" onClick={() => makeMain(i)} data-testid={`image-main-${i}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-accent-brand text-white text-[10px] font-bold uppercase tracking-widest py-1.5 flex items-center justify-center gap-1"
                  ><Star size={12} /> Photo principale</button>
                )}
              </div>
              {i === 0 && (
                <div className="absolute top-1.5 left-1.5 bg-accent-brand text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
                  <Star size={10} fill="currentColor" /> Principale
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-navy/50">La première photo est utilisée comme visuel principal. Glissez-déposez pour réordonner.</p>
    </div>
  );
}
